import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    halfWidth: {},
    thirdWidth: {},
    modal: {
        height: 'calc(100dvh - 32px)',
        fallbacks: { height: 'calc(100vh - 32px)' },
        width: 'min(1000px, calc(100vw - 32px))',
    },
    form: {
        width: '100%',
    },
    modalContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    modalHeader: {
        flexShrink: 0,
        width: '100%',
        background: theme.isDark
            ? 'linear-gradient(135deg, #0d0a1e 0%, #1a1040 55%, #0a1a2e 100%)'
            : `linear-gradient(135deg, ${theme.colors.violet_darkest} 0%, ${theme.colors.violet_primary} 55%, ${theme.colors.deep_blue} 100%)`,

        padding: '22px 28px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '-55px',
            right: '-35px',
            width: 210,
            height: 210,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color(theme.colors.blue_neon).alpha(0.22).string()} 0%, transparent 70%)`,
            filter: 'blur(22px)',
            pointerEvents: 'none',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-42px',
            left: '28%',
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color(theme.colors.violet_primary).alpha(0.5).string()} 0%, transparent 70%)`,
            filter: 'blur(20px)',
            pointerEvents: 'none',
        },
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        zIndex: 1,
        position: 'relative',
        minWidth: 0,
    },
    headerIcon: {
        width: 46,
        height: 46,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${color(theme.colors.blue_primary).alpha(0.55).string()}, ${color(theme.colors.blue_neon).alpha(0.45).string()})`,
        backdropFilter: 'blur(8px)',
        border: '2px solid rgba(255, 255, 255, 0.28)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: theme.font.secondary,
        fontSize: 18,
        color: '#ffffff',
        flexShrink: 0,
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.28)',
        letterSpacing: '0.04em',
        userSelect: 'none',
        textTransform: 'uppercase' as const,
    },
    h2: {
        fontFamily: theme.font.secondary,
        fontSize: 21,
        lineHeight: 1.3,
        color: '#ffffff',
        fontWeight: 'normal',
        margin: 0,
    },
    headerSubtitle: {
        fontSize: 12,
        color: 'rgba(255, 255, 255, 0.58)',
        fontFamily: theme.font.primary,
        margin: '2px 0 0',
        lineHeight: 1.4,
    },
    imageContainer: {
        zIndex: 1,
        position: 'relative',
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
        border: '1.5px solid rgba(255, 255, 255, 0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 0.2s ease, border-color 0.2s ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.22)',
            borderColor: 'rgba(255, 255, 255, 0.38)',
        },
        '& img': {
            width: 14,
            height: 14,
            filter: 'brightness(0) invert(1)',
        },
    },
    modalBody: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflowY: 'auto',
        padding: '22px 28px 16px',
        background: theme.colors.grey_background,
        '&::-webkit-scrollbar': {
            width: 4,
        },
        '&::-webkit-scrollbar-track': {
            width: 4,
            borderRadius: 2,
            backgroundColor: color(theme.colors.grey_darker).fade(0.75).string(),
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 2,
            backgroundColor: theme.colors.grey_darker,
        },
    },
    section: {
        background: theme.colors.white,
        borderRadius: 8,
        border: `1px solid ${theme.colors.grey_light}`,
        padding: '14px 20px 4px',
        marginBottom: 14,
        position: 'relative',
        overflow: 'visible',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${theme.colors.violet_primary}, ${theme.colors.blue_primary})`,
            borderRadius: '8px 8px 0 0',
        },
    },
    sectionTitle: {
        fontFamily: theme.font.secondary,
        fontSize: 10,
        letterSpacing: '0.1em',
        textTransform: 'uppercase' as const,
        color: theme.colors.violet_primary,
        margin: '6px 0 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        '&::after': {
            content: '""',
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, ${theme.colors.grey_light}, transparent)`,
        },
    },
    brandingLayout: {
        display: 'flex',
        gap: 20,
        alignItems: 'flex-start',
        [theme.down('tablet')]: {
            flexDirection: 'column',
        },
    },
    brandingLeft: {
        flex: 1,
        minWidth: 0,
    },
    nameWithToggle: {
        display: 'flex',
        gap: 14,
        alignItems: 'flex-end',
        marginBottom: theme.spacing(6),
    },
    toggleInline: {
        flexShrink: 0,
        paddingBottom: 4,
    },
    brandingRight: {
        width: 170,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 6,
        paddingTop: 2,
        [theme.down('tablet')]: {
            width: '100%',
        },
    },
    brandingLogoLabel: {
        fontFamily: theme.font.secondary,
        fontSize: 9,
        letterSpacing: '0.1em',
        textTransform: 'uppercase' as const,
        color: theme.colors.grey_text,
        marginBottom: 2,
    },
    field: {
        marginBottom: theme.spacing(6),
        '&$halfWidth': {
            width: 'calc(50% - 12px)',
        },
        '&$thirdWidth': {
            width: 'calc(33% - 12px)',
        },
        [theme.down('tablet')]: {
            width: '100% !important',
        },
    },
    fieldWrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    dropdown: {
        margin: 0,
    },
    buttons: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '14px 0 4px',
        flexWrap: 'wrap',
        gap: theme.spacing(3),
        marginTop: 6,
        borderTop: `1px solid ${theme.colors.grey_light}`,
    },
    end: {
        justifyContent: 'flex-end',
    },
    button: {
        marginLeft: theme.spacing(5),
    },
    errors: {
        // ...stickyFormBlock(),
    },
    error: {
        backgroundColor: theme.colors.red,
        marginBottom: theme.spacing(2),
    },
    helperWrapper: {
        fontSize: 16,
        lineHeight: '20px',
        color: theme.colors.blue_primary,
        fontFamily: theme.font.secondary,
        marginBottom: theme.spacing(4),
    },
}));
